import {
  Box,
  Stack,
  Theme,
  Button,
  Divider,
  useTheme,
  Typography,
} from "@mui/material";
import NextImage from "next/image";
import { CgCheck } from "react-icons/cg";
import {
  EventCardNoInventoryMessage,
  EventCardWarningMessage,
  EventCardTag,
} from "./event_card_helpers";
import { convertDate, convertTime } from "@/global/utils/dateTimeConvert";
import { MarqueeText } from "@/shared/common/layouts/MarqueeText/MarqueeText";
import { AnimatedCard } from "@/shared/base/components/AnimatedCard/AnimatedCard";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { useEventCardCommon } from "./useEventCardCommon";
import { EventCardProps } from "./CombinedEventCard";
import { blurDataURL } from "@/global/common/blur-data-url";

export function UpcomingEventCard(props: EventCardProps) {
  const theme = useTheme();
  const styles = useStyles(theme, props);
  const { sm: smDown } = useCssMediaQuery("down");
  const { isSelected, onSelect } = props;
  const {
    isDisabled,
    isMarqueeDisabled,
    isWithinSeatLimit,
    isPreviewButtonEnabled,
    soldOutLabelDetails,
    isSoldOut,
    customTagSize,
    customMarqueeMinLength,
    hideSoldOutTag
  } = useEventCardCommon(props);

  return (
    <Box position="relative">
      <Box
        sx={{
          pr: 2,
          top: 0,
          right: 0,
          zIndex: 2,
          position: "absolute",
          transform: "translate(0%, -50%)",
        }}
      >
        {isSoldOut && !hideSoldOutTag ? (
          <EventCardTag
            label={soldOutLabelDetails?.label}
            color={soldOutLabelDetails?.color || theme.palette.primary.main}
            isMarqueeDisabled={isMarqueeDisabled}
          />
        ) : null}
      </Box>

      <AnimatedCard
        variant={props?.cardVariant}
        borderRadius={"10px"}
        footerHeight={isPreviewButtonEnabled ? "100px" : "70px"}
        isSelected={props.isSelected}
        isDisabled={isDisabled}
        onClick={onSelect}
        renderBody={() => {
          return (
            <Stack height={"100%"}>
              <Box p={2} py={1.5}>
                <Stack
                  direction={"row"}
                  justifyContent={"flex-start"}
                  alignItems={"center"}
                  spacing={2}
                >
                  <Box width="48px" height={"48px"}>
                    {props?.event?.logoUrl ? (
                      <NextImage
                        src={props?.event?.logoUrl}
                        alt={"StadiumBackground team logo"}
                        width={48}
                        height={48}
                        style={{ objectFit: "contain", borderRadius: "4px" }}
                        loading="lazy"
                        placeholder="blur"
                        blurDataURL={blurDataURL}
                      />
                    ) : null}
                  </Box>

                  <Box
                    textAlign={"left"}
                    height={smDown && !isMarqueeDisabled ? "48px" : "unset"} // prettier-ignore
                    width={"100%"}
                  >
                    {smDown && !isMarqueeDisabled ? (
                      <MarqueeText
                        text={props?.event?.title}
                        minLength={16}
                        style={{
                          fontSize: "1rem",
                          fontWeight: 700,
                        }}
                      />
                    ) : (
                      <Typography fontSize={"1rem"} fontWeight={700}>
                        {props?.event?.title}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Box>
              <Divider />
              <Box padding={"1rem"} flexGrow={1}>
                <Stack direction={"column"} height="100%">
                  {smDown && !isMarqueeDisabled ? (
                    <Box height={"22px"} width={"100%"}>
                      <MarqueeText
                        text={`${convertDate(
                          props?.event?.date
                        )} | ${convertTime(props?.event?.time)}`}
                        minLength={16}
                        style={{
                          fontSize: "0.9rem",
                          fontWeight: "500",
                        }}
                      />
                    </Box>
                  ) : (
                    <Stack
                      direction={"row"}
                      gap="0.5rem"
                      alignItems={"center"}
                      flexGrow={1}
                    >
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        fontSize={"0.9rem"}
                        fontWeight={"500"}
                      >
                        {convertDate(props?.event?.date)}
                      </Typography>
                      <Divider
                        orientation="vertical"
                        sx={{ height: "8px !important" }}
                      />
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        fontSize={"0.9rem"}
                        fontWeight={"500"}
                      >
                        {convertTime(props?.event?.time)}
                      </Typography>
                    </Stack>
                  )}

                  {!isWithinSeatLimit ? (
                    <Box mt={1}>
                      <EventCardWarningMessage
                        description={
                          props.event.maxSeatLimit == 0
                            ? "This offer is no longer available."
                            : "You have purchased your maximum ticket limit for this event."
                        }
                      />
                    </Box>
                  ) : null}

                  {props?.event?.hasInventory !== undefined &&
                    !props?.event?.hasInventory &&
                    isWithinSeatLimit ? (
                    <Box mt={0.5}>
                      <EventCardNoInventoryMessage />
                    </Box>
                  ) : null}
                </Stack>
              </Box>

              <Stack
                sx={styles.iconTagWrapper}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
                width="100%"
                spacing={1}
              >
                <Box pl={2}>
                  <Box sx={styles.checkIcon}>
                    <CgCheck />
                  </Box>
                </Box>
                <Box flexGrow={1} />

                <Box>
                  {!isSoldOut && props?.event?.label ? (
                    <Box
                      borderRadius={2}
                      width={"135px"}
                      height={"24px"}
                      bgcolor={
                        props?.event?.labelHex || theme.palette.primary.main
                      }
                      px={1}
                    >
                      <MarqueeText
                        text={props?.event?.label}
                        minLength={16}
                        style={{
                          fontSize: "12px",
                          fontWeight: 500,
                          color: theme.palette.common.white,
                        }}
                      />
                    </Box>
                  ) : null}
                </Box>

                <Box pr={2}>
                  {!isSoldOut && props?.event?.tag ? (
                    <EventCardTag
                      label={props?.event?.tag}
                      color={props?.event?.tagHex || theme.palette.primary.main}
                      isMarqueeDisabled={isMarqueeDisabled}
                      width={customTagSize}
                      customMarqueeMinLength={customMarqueeMinLength}
                    />
                  ) : null}
                </Box>
              </Stack>
            </Stack>
          );
        }}
        renderFooter={() => {
          return (
            <Box width="100%" p={2}>
              <Stack
                spacing={isPreviewButtonEnabled ? 1 : 2}
                direction={"column"}
              >
                {isPreviewButtonEnabled ? (
                  <Button
                    variant={"outlined"}
                    sx={{ width: "100%" }}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      props?.onClickPreview?.();
                    }}
                  >
                    Preview
                  </Button>
                ) : null}

                <Button
                  variant={isSelected ? "outlined" : "contained"}
                  sx={{ width: "100%" }}
                  onSelect={onSelect}
                >
                  {isSelected ? "Unselect" : "Select +"}
                </Button>
              </Stack>
            </Box>
          );
        }}
      ></AnimatedCard>
    </Box>
  );
}

function useStyles(theme: Theme, states: Partial<EventCardProps>) {
  const primaryColor = theme.palette.primary.main;
  const paperBackground = theme.palette.background.paper;
  const cardCubicBezire = "cubic-bezier(.27,.29,0,1)";

  return {
    iconTagWrapper: {
      position: "absolute",
      top: 0,
      left: 0,
      zIndex: 1,
      transform: "translate(0%, -50%) scale(1)",
      overflow: "hidden",
    },

    checkIcon: {
      fontSize: "24px",
      color: primaryColor,
      width: "24px",
      height: "24px",
      backgroundColor: paperBackground,
      borderRadius: "50%",
      border: `2px solid ${primaryColor}`,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      opacity: states.isSelected ? 1 : 0,
      transition: `all 0.2s ${cardCubicBezire}`,
      transform: `translateX(${states.isSelected ? 0 : "-10%"})`,
    },
  };
}
